import { useState, useRef, useEffect, useCallback } from "react";
import { cleanupAudioCache, getCachedAudioUrl, cacheAudioBackground, prefetchNextChapterAudio } from "@/lib/audio-cache";
interface UseChapterAudioProps {
  storySlug: string;
  chapterId: string;
  nextChapterId?: string;
  isAuthenticated?: boolean;
  autoPlay?: boolean;
  onChapterEnd?: () => void;
}
let globalAudioElement: HTMLAudioElement | null = null;
function getGlobalAudio() {
  if (typeof window === 'undefined') return null;
  if (!globalAudioElement) {
    globalAudioElement = new Audio();
    globalAudioElement.setAttribute("playsinline", "true");
  }
  return globalAudioElement;
}
function setupMediaSessionControls(audio: HTMLAudioElement, skip: (seconds: number) => void, onChapterEnd?: () => void) {
  if (!("mediaSession" in navigator)) return;
  navigator.mediaSession.metadata = new MediaMetadata({
    title: "Audio Chapter",
    artist: "Iloris Reader"
  });
  navigator.mediaSession.setActionHandler('play', () => audio.play());
  navigator.mediaSession.setActionHandler('pause', () => audio.pause());
  navigator.mediaSession.setActionHandler('seekbackward', () => skip(-30));
  navigator.mediaSession.setActionHandler('seekforward', () => skip(30));
  navigator.mediaSession.setActionHandler('seekto', details => {
    if (details.seekTime && audio) {
      audio.currentTime = details.seekTime;
    }
  });
  if (onChapterEnd) {
    navigator.mediaSession.setActionHandler('nexttrack', () => onChapterEnd());
  } else {
    navigator.mediaSession.setActionHandler('nexttrack', null);
  }
}
export function useChapterAudio({
  storySlug,
  chapterId,
  nextChapterId,
  isAuthenticated,
  autoPlay,
  onChapterEnd
}: UseChapterAudioProps) {
  // All refs declared at the top — before any effects that reference them.
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);
  const hasAutoPlayedRef = useRef(false);
  const pendingPlayOnVisibleRef = useRef(false);
  // Drag state never needs to trigger a re-render — it's only read inside the
  // timeupdate handler to suppress position updates while the user is scrubbing.
  const isDraggingRef = useRef(false);
  // Always points to the latest onChapterEnd / autoPlay without requiring them as
  // deps in loadAndPlayAudio or the event-listener effect. Prevents stale closures.
  const onChapterEndRef = useRef(onChapterEnd);
  const isAutoPlayRef = useRef(!!autoPlay);
  // Pre-fetched blob URL for the next chapter, populated after prefetch completes.
  // Used by the 'ended' handler for gapless locked-screen autoplay.
  const nextChapterAudioUrlRef = useRef<string | null>(null);
  // Set when the 'ended' handler starts the next chapter immediately (gapless path).
  // Tells the chapterId cleanup to skip the pause logic — React is catching up to
  // audio that's already playing.
  const isAutoAdvanceRef = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Keep callback/prop refs in sync on every render. No dep array — always want
  // the latest value so event handlers never call a stale callback.
  useEffect(() => {
    onChapterEndRef.current = onChapterEnd;
    isAutoPlayRef.current = !!autoPlay;
  });

  // When the screen unlocks/tab returns to foreground, resume pending autoplay.
  // This is the fallback for when gapless transition wasn't possible (e.g. the
  // next chapter hadn't finished prefetching before the current chapter ended).
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && pendingPlayOnVisibleRef.current) {
        pendingPlayOnVisibleRef.current = false;
        const audio = audioRef.current;
        if (audio && audio.paused && audio.readyState >= 2) {
          audio.play().then(() => setIsPlaying(true)).catch(console.error);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Clean up when unmounting or chapter changes.
  useEffect(() => {
    if (!isAutoAdvanceRef.current) {
      // Normal chapter change: reset all playback state.
      setAudioUrl(null);
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
    } else if (nextChapterId) {
      // Gapless transition: audio is already playing the new chapter.
      // loadAndPlayAudio won't run (audioRef is set + not paused), so we
      // must manually kick off the prefetch for the NEXT-next chapter here
      // to keep the gapless chain going.
      cleanupAudioCache(chapterId, nextChapterId).catch(() => {});
      prefetchNextChapterAudio(storySlug, nextChapterId).then(() => getCachedAudioUrl(nextChapterId)).then(url => {
        if (url) nextChapterAudioUrlRef.current = url;
      }).catch(() => {});
    }
    // Always reset these regardless of path.
    isAutoAdvanceRef.current = false;
    hasAutoPlayedRef.current = false;
    pendingPlayOnVisibleRef.current = false;
    nextChapterAudioUrlRef.current = null; // new chapter will prefetch its own next
    isFetchingRef.current = false;
    return () => {
      if (isAutoAdvanceRef.current) {
        // Gapless transition: audio is already playing the next chapter.
        // React is catching up — don't pause or clear the audio ref.
        return;
      }
      if (audioRef.current) {
        // User navigated away mid-chapter: pause cleanly.
        if (!audioRef.current.ended && !audioRef.current.paused) {
          audioRef.current.pause();
          audioRef.current.removeAttribute("src");
          audioRef.current.load();
        }
      }
      // Clear the React ref so the next chapter knows it must load new media,
      // but DO NOT destroy the globalAudioElement singleton.
      audioRef.current = null;
      if (objectUrlRef.current) {
        const urlToRevoke = objectUrlRef.current;
        setTimeout(() => URL.revokeObjectURL(urlToRevoke), 10000);
        objectUrlRef.current = null;
      }
    };
  }, [chapterId]);

  // Attach audio element event listeners.
  // Deps: only [audioUrl] — listeners are only re-attached when the audio source
  // changes. isDragging and onChapterEnd are read via stable refs so they never
  // force a listener teardown/re-add on drag events or parent re-renders.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTimeUpdate = () => {
      if (!isDraggingRef.current) {
        setCurrentTime(audio.currentTime);
      }
    };
    const onLoadedMetadata = () => setDuration(audio.duration);
    const onEnded = () => {
      const nextUrl = nextChapterAudioUrlRef.current;
      if (isAutoPlayRef.current && nextUrl) {
        // ── Gapless locked-screen autoplay ──────────────────────────────
        // We're inside the 'ended' media event. Chromium (Brave/Android)
        // still considers this a trusted media context, so audio.play() is
        // permitted even while the screen is locked — no new user gesture
        // needed. This is the same mechanism used by gapless web players.
        //
        // Call play() NOW before returning control to the browser. Anything
        // async (router.push, React re-renders) happens concurrently.
        isAutoAdvanceRef.current = true;
        nextChapterAudioUrlRef.current = null;
        const prevObjectUrl = objectUrlRef.current;
        objectUrlRef.current = nextUrl;
        audio.src = nextUrl;
        audioRef.current = audio;
        setAudioUrl(nextUrl); // triggers event-listener re-attachment for new chapter
        audio.load();
        audio.play().catch(err => {
          // If play() was still blocked (e.g. iOS), fall back to pending.
          pendingPlayOnVisibleRef.current = true;
          if ('mediaSession' in navigator) {
            navigator.mediaSession.playbackState = 'paused';
          }
          console.warn("Gapless play blocked, will resume on unlock:", err);
        });
        if (prevObjectUrl) {
          setTimeout(() => URL.revokeObjectURL(prevObjectUrl), 10000);
        }

        // Navigate React state concurrently — UI catches up to audio.
        onChapterEndRef.current?.();
        return;
      }

      // ── Normal path ─────────────────────────────────────────────────────
      // Either autoplay is off, or the next chapter URL wasn't ready yet
      // (short chapter, slow network). Reset UI and hand off to the normal
      // chapter-end flow. If the screen is locked the pending flag will
      // resume playback once the user unlocks.
      setIsPlaying(false);
      setCurrentTime(audio.duration);
      onChapterEndRef.current?.();
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
    };
  }, [audioUrl]);

  // Stable skip — reads duration directly from the audio element so the function
  // never captures stale state and doesn't need to be in any dep array.
  const skip = useCallback((seconds: number) => {
    const audio = audioRef.current;
    if (audio) {
      const newTime = Math.max(0, Math.min(audio.currentTime + seconds, audio.duration));
      audio.currentTime = newTime;
      setCurrentTime(newTime);
    }
  }, []);
  const loadAndPlayAudio = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);
    try {
      const res = await fetch(`/api/tts?storySlug=${encodeURIComponent(storySlug)}&chapterId=${encodeURIComponent(chapterId)}`);
      const data = await res.json();
      if (!res.ok) {
        const err = Object.assign(new Error(data.error || "Failed to load audio"), {
          code: data.code
        });
        throw err;
      }
      let finalUrl = data.url;
      await cleanupAudioCache(chapterId, nextChapterId);
      const cachedUrl = await getCachedAudioUrl(chapterId);
      if (cachedUrl) {
        finalUrl = cachedUrl;
        objectUrlRef.current = finalUrl;
      } else {
        cacheAudioBackground(chapterId, data.url).catch(() => {});
      }
      setAudioUrl(finalUrl);
      if (nextChapterId) {
        // Prefetch next chapter's audio file into the browser cache, then
        // immediately resolve its blob URL into nextChapterAudioUrlRef so
        // the 'ended' handler can start it without any async work.
        prefetchNextChapterAudio(storySlug, nextChapterId).then(() => getCachedAudioUrl(nextChapterId)).then(url => {
          if (url) nextChapterAudioUrlRef.current = url;
        }).catch(() => {});
      }
      const audio = getGlobalAudio();
      if (!audio) throw new Error("Audio not supported");
      audio.src = finalUrl;
      audioRef.current = audio;
      await new Promise<void>((resolve, reject) => {
        const onLoaded = () => {
          audio.removeEventListener("loadedmetadata", onLoaded);
          audio.removeEventListener("error", onError);
          resolve();
        };
        const onError = () => {
          audio.removeEventListener("loadedmetadata", onLoaded);
          audio.removeEventListener("error", onError);
          reject(new Error("Failed to load audio stream"));
        };
        audio.addEventListener("loadedmetadata", onLoaded);
        audio.addEventListener("error", onError);
        // Required to reliably trigger loadedmetadata after changing src on an existing element.
        audio.load();
      });
      setDuration(audio.duration);

      // Set up media session controls BEFORE attempting play so the lock screen
      // always shows the correct chapter controls — even if play() is about to
      // be blocked because the screen is locked. onChapterEnd is read via ref
      // so the nexttrack handler always calls the current callback, not a stale one.
      setupMediaSessionControls(audio, skip, () => onChapterEndRef.current?.());
      if (document.hidden) {
        // Screen is locked: play() would be blocked by autoplay policy.
        // Mark as pending — the visibilitychange handler will play when the
        // screen wakes, and the lock screen controls let the user manually
        // trigger play via the media session 'play' action.
        pendingPlayOnVisibleRef.current = true;
        // Explicitly signal 'paused' so the OS keeps the lock screen media
        // widget visible with a play button. Without this the browser resets
        // playbackState to 'none' after ended and the widget disappears.
        if ('mediaSession' in navigator) {
          navigator.mediaSession.playbackState = 'paused';
        }
      } else {
        try {
          await audio.play();
          setIsPlaying(true);
        } catch (playError: unknown) {
          if (playError instanceof Error && playError.name === 'NotAllowedError') {
            // Autoplay blocked (race: screen locked between the hidden check and play()).
            pendingPlayOnVisibleRef.current = true;
          } else {
            throw playError;
          }
        }
      }
    } catch (err: unknown) {
      console.error("Audio play error:", err);
      setError(err instanceof Error ? err.message : "Something went wrong.");
      setErrorCode(err instanceof Error && "code" in err && typeof err.code === "string" ? err.code : null);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [storySlug, chapterId, nextChapterId, skip]);
  const playAudio = useCallback(async () => {
    setError(null);
    setErrorCode(null);
    if (isAuthenticated === false) {
      setError("Please log in to use Text-to-Speech.");
      setErrorCode("login_required");
      return;
    }
    if (!audioRef.current) {
      await loadAndPlayAudio();
      return;
    }
    if (audioRef.current.paused) {
      audioRef.current.play().catch((e: Error) => {
        console.error("Error resuming audio:", e);
        setError("Could not resume audio stream.");
      });
    }
  }, [isAuthenticated, loadAndPlayAudio]);
  const togglePlay = useCallback(async () => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
    } else {
      await playAudio();
    }
  }, [playAudio]);

  // Autoplay effect.
  // By tracking execution using a dedicated ref independent of playAudio's identity,
  // we avoid infinite loops or forcing re-plays when dependency arrays change.
  useEffect(() => {
    if (autoPlay && !hasAutoPlayedRef.current) {
      hasAutoPlayedRef.current = true;
      playAudio();
    } else if (autoPlay && hasAutoPlayedRef.current) {
      // If the user toggles autoplay on mid-chapter, ensure it actually kicks off.
      if (audioRef.current && audioRef.current.paused) {
        playAudio();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, chapterId]); // Purposely omitting playAudio to prevent unintended re-fetches

  const handleSeek = (time: number) => {
    setCurrentTime(time);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
  };

  // Exposed as a stable callback — drag state doesn't need to trigger re-renders
  // since it's only read inside event handlers via isDraggingRef.
  const setIsDragging = useCallback((val: boolean) => {
    isDraggingRef.current = val;
  }, []);
  return {
    audioUrl,
    isPlaying,
    isLoading,
    error,
    errorCode,
    currentTime,
    duration,
    togglePlay,
    handleSeek,
    setIsDragging,
    skip
  };
}